import { colorsPalettes } from "@similarweb/styles";
import { FlexColumn, OnOffSwitch, RadioButton } from "@similarweb/ui-components";

import { TextWithInfoTooltip } from "app/pages/activation/components/Shared/TextWithInfoTooltip/TextWithInfoTooltip";
import type { SubscriptionProviderReturnType } from "app/pages/activation/context/Subscription/types";
import { translate } from "app/filters/i18nFilter";
import * as SC from "app/pages/activation/components/Shared/Subscriptions/styles";
import {
    COMPETITORS_ALERTS_TITLE,
    COMPETITORS_ALERTS_TOOLTIP,
    FREQUENCY_TEXT,
    RECEIVE_ALERT_TEXT,
} from "app/pages/activation/components/Shared/Subscriptions/texts";
import { ActivationSubscriptions } from "app/pages/activation/components/Shared/Subscriptions/types";

import { generateAutomationName } from "../../../helpers/generateAutomationName";
import type { BenchmarkGranularity } from "../../../types/common";

export const ALERTS_SUBSCRIPTIONS_WRAPPER_ID = "alerts-subscriptions-wrapper";

type AlertsSubscriptionsSchema = {
    alertsGranularity: SubscriptionProviderReturnType["alertsGranularity"];
    granularityList: BenchmarkGranularity[];
    isSwitchedAlert: SubscriptionProviderReturnType["isSwitchedAlert"];
    onSwitch: SubscriptionProviderReturnType["onSwitch"];
    onSelect: SubscriptionProviderReturnType["onSelect"];
    isAlertsAvailable: SubscriptionProviderReturnType["isAlertsAvailable"];
};

export const AlertsSubscriptions = ({
    alertsGranularity,
    granularityList,
    isSwitchedAlert,
    onSwitch,
    onSelect,
    isAlertsAvailable,
}: AlertsSubscriptionsSchema) => {
    const onAlertSwitch = () => onSwitch(!isSwitchedAlert, ActivationSubscriptions.CompetitorsAlerts);

    const onAlertsGranularitySelect = (granularity: BenchmarkGranularity) => () =>
        onSelect(granularity, ActivationSubscriptions.CompetitorsAlerts);

    if (!isAlertsAvailable) {
        return null;
    }

    return (
        <FlexColumn
            id={ALERTS_SUBSCRIPTIONS_WRAPPER_ID}
            data-automation={generateAutomationName("settings", ["subscriptions", "alerts", "container"])}
        >
            <TextWithInfoTooltip
                title={COMPETITORS_ALERTS_TITLE}
                tooltip={COMPETITORS_ALERTS_TOOLTIP}
                iconColor={colorsPalettes.carbon[200]}
                dataAutomation={generateAutomationName("settings", ["subscriptions", "alerts", "title"])}
                marginBottom={4}
            />

            <SC.SwitchWrapper
                data-automation={generateAutomationName("settings", ["subscriptions", "alerts", "switch", "container"])}
            >
                <SC.SwitchLabel
                    onClick={onAlertSwitch}
                    data-automation={generateAutomationName("settings", ["subscriptions", "alerts", "switch", "label"])}
                >
                    {translate(RECEIVE_ALERT_TEXT)}
                </SC.SwitchLabel>

                <OnOffSwitch
                    onClick={onAlertSwitch}
                    isSelected={isSwitchedAlert}
                />
            </SC.SwitchWrapper>

            <SC.RadioGroupTitle
                data-automation={generateAutomationName("settings", [
                    "subscriptions",
                    "alerts",
                    "radio",
                    "group",
                    "title",
                ])}
            >
                {translate(FREQUENCY_TEXT)}
            </SC.RadioGroupTitle>

            <SC.RadioGroup
                data-automation-isDisabled={!isSwitchedAlert}
                data-automation={generateAutomationName("settings", [
                    "subscriptions",
                    "alerts",
                    "granularity",
                    "radio",
                    "group",
                    "container",
                ])}
            >
                {granularityList.map((granularity, id) => (
                    <RadioButton
                        itemDisabled={!isSwitchedAlert}
                        className="emailGranularity"
                        key={id}
                        itemLabel={granularity}
                        onClick={onAlertsGranularitySelect(granularity)}
                        itemSelected={granularity === alertsGranularity}
                    />
                ))}
            </SC.RadioGroup>
        </FlexColumn>
    );
};
